import { Actions, Async, FSpread } from '@client/new/helpers/types';
import {
  GetHomePageDataQuery,
  ProgramAccessType,
  useCreateDraftCollectionMutation,
  useGetHomePageDataQuery,
  useRequestConvertToPpcMutation,
  useSetCreatorAsLiveMutation,
} from 'generated/graphql';
import { useUserStore } from '@client/new/stores/user.store';
import { browserHistory } from 'react-router';
import { PATHS } from '@client/routes';
import toast from 'react-hot-toast';
import { useState } from 'react';

type HomeModel = {
  dispatch: HomeModelDispatcher;
  submittingOffer: boolean;
  goLiveProgress: boolean;
  uid: string;
  data: GetHomePageDataQuery;
  creatorWentLive: boolean;
  influencerId: number;
};

export type PayloadMap = {
  createOffer: Offer;
  goLive: null;
};
export type HomeModelActions = Actions<PayloadMap>;
export type HomeModelDispatcher = FSpread<PayloadMap>;

export type CollectionType = 'otp' | 'subscription';

export type Offer = {
  name: string;
  type: CollectionType;
  subscriptionType?: 'ppc' | 'platform';
};

export const useHomeModel = (p: {
  influencerId?: string;
}): Async<HomeModel> => {
  const [creatorWentLive, setCreatorWentLive] = useState(false);
  const { user } = useUserStore();
  const influencerId = p?.influencerId
    ? Number(p?.influencerId)
    : user.influencerId;

  const { data, loading, error, refetch } = useGetHomePageDataQuery({
    variables: {
      creatorId: influencerId,
    },
    skip: !influencerId,
    fetchPolicy: 'network-only',
  });

  const [setCreatorAsLiveMutation, setCreatorAsLiveMutationState] =
    useSetCreatorAsLiveMutation({
      variables: {
        creatorId: influencerId,
      },
    });

  const [createDraftCollectionMutation, useCreateDraftCollectionMutationState] =
    useCreateDraftCollectionMutation();

  const [requestConvertToPpcMutation] = useRequestConvertToPpcMutation();

  const uid = `${user.id}`;

  const dispatch: HomeModelDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as HomeModelActions;
    switch (action.type) {
      case 'createOffer':
        createOffer(action.payload);
        return null;
      case 'goLive':
        goLive();
        return null;
    }
  };

  if (loading) return { type: 'fetching' };

  if (error || !data?.creator)
    return { type: 'fetch-error', value: 'Could not fetch data' };

  const influencer = data?.creator;
  const isPpcRequested = !!influencer?.customizations?.ppcConversionRequested;
  const isPPC = influencer?.visibility === 'exclusive';

  const requestPPC = async () => {
    try {
      requestConvertToPpcMutation({
        variables: {
          creatorId: influencerId,
        },
      }).then((res) => {
        if (res.errors) {
          toast.error('Could not request conversion to PPC');
        }
      });
    } catch (er) {
      toast.error('Could not request conversion to PPC');
    }
  };

  const createOffer = async (offer: Offer) => {
    const accessType =
      offer.type === 'otp'
        ? ProgramAccessType.Exclusive
        : ProgramAccessType.Premium;

    if (
      offer.type === 'subscription' &&
      offer.subscriptionType === 'ppc' &&
      (!isPpcRequested || !isPPC)
    ) {
      await requestPPC();
    }

    createDraftCollectionMutation({
      variables: {
        data: {
          accessType,
          creatorId: influencer.id,
          name: offer.name,
          type: 0,
        },
      },
    })
      .then(() => {
        const path =
          offer.type === 'otp'
            ? PATHS.PURCHASES(influencer.id)
            : PATHS.SUBSCRIPTIONS(influencer.id);
        browserHistory.push(path);
      })
      .catch((error) => {
        toast.error(error.message);
      });
  };

  const goLive = () => {
    setCreatorAsLiveMutation().then(() => {
      setCreatorWentLive(true);
      refetch();
    });
  };

  if (loading) return { type: 'fetching' };

  if (error || !data?.creator)
    return { type: 'fetch-error', value: 'Could not fetch data' };

  return {
    type: 'fetched',
    value: {
      dispatch,
      submittingOffer: useCreateDraftCollectionMutationState.loading,
      goLiveProgress: setCreatorAsLiveMutationState.loading,
      uid,
      data,
      creatorWentLive,
      influencerId,
    },
  };
};
